// The family's credit (money paid above cost on its registrations, and money
// left on cancelled or denied ones) applied to what it owes. Camp store
// funds are never credit, and credit never buys camp store funds. The portal
// API works every amount out again (src/repositories/CreditRepo.ts) and
// refuses more than the credit or more than is owed; these only say on the
// screen what it will do.

const cents = (value: unknown) => Math.round((Number(value) || 0) * 100) / 100;

// the part of a payment credit cannot pay: camp store funds bought with a
// registration, store top-ups, and the rest of a payment plan
export const notPayableByCredit = (costs: any[]) =>
  cents(
    (costs || []).reduce(
      (total: number, cost: any) => total + (Number(cost && cost.cost) || 0),
      0
    )
  );

export const isStoreCost = (cost: any) =>
  !!(cost && (cost.isFundBucket || cost.isTotalFunds));

// Pay & Complete, paying in full: credit pays up to the smaller of the
// credit and what it can pay of the amount due now
export const creditForPayment = (
  balanceCredit: number,
  totalCost: number,
  notPayable = 0
) =>
  cents(
    Math.max(
      0,
      Math.min(cents(balanceCredit), cents(totalCost) - cents(notPayable))
    )
  );

// what the card is charged after the credit
export const cardAfterCredit = (totalCost: number, credit: number) =>
  cents(Math.max(0, cents(totalCost) - cents(credit)));

// the Balances tab: credit applied to one registration that owes
export const creditForBalance = (balanceCredit: number, balance: number) =>
  cents(Math.max(0, Math.min(cents(balanceCredit), cents(balance))));

// Pay Monthly with credit: the credit comes off the plan before its payments
// are worked out (Amanda, 2026-10-15). As paying in full, it pays each
// registration's fee, then its add-ons, never camp store funds. The portal
// API works the credit and the payments out (getPaymentInfo with applyCredit
// answers monthlyCredit: creditApplied, firstCharge, recurringAmount, their
// Formatted text) and the screen shows those; null when the credit leaves
// nothing for a plan (then it is Pay in Full).
export const monthlyWithCredit = (monthlyCredit: any) =>
  monthlyCredit &&
  Number(monthlyCredit.creditApplied) > 0 &&
  Number(monthlyCredit.recurringAmount) > 0
    ? monthlyCredit
    : null;

// the Balances tab: what the family is told once credit is applied (the
// API's answer to /finance/applyCredit): what the API applied, which can be
// less than asked (a plan's monthly payment cannot go under $0.50, and the
// rest stays as credit); for a registration on a payment plan, its new
// monthly payment, unless the plan no longer bills at the card processor
// (planNotBilling: the credit was a plain payment and the plan is
// unchanged); then the API's notice saying why, when there is one
export const appliedCreditMessage = (data: any, amount: number) => {
  const money = (value: number) =>
    new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(
      cents(value)
    );
  const answer = data || {};
  const fromApi =
    answer.applied !== undefined &&
    answer.applied !== null &&
    answer.applied !== "" &&
    Number.isFinite(Number(answer.applied));
  const applied = fromApi
    ? money(Number(answer.applied))
    : answer.appliedFormatted || money(amount);
  const plan = answer.planNotBilling ? null : answer.plan;
  let message = `Applied ${applied} of credit`;
  if (plan && plan.paidOff) {
    message += ". That pays off the payment plan.";
  } else if (plan) {
    message += `. The monthly payment is now ${money(plan.planMonthly)}${
      cents(plan.nextCharge) > cents(plan.planMonthly)
        ? ` (${money(plan.nextCharge)} on the next one)`
        : ""
    }.`;
  }
  const notice = String(answer.notice || "").trim();
  if (!notice) {
    return message;
  }
  return `${/[.!?]$/.test(message) ? message : `${message}.`} ${notice}`;
};

// New Payment/Plan (and PAY NOW): whether the registrations chosen can only
// be paid in full. A registration already on a payment plan is (its plan
// charges what is left), and so is one whose plan was cancelled (step 4).
// A registration part paid with no plan, e.g. credit applied to part of it
// on the Balances tab, can go on a plan for what it still owes, with the
// same plan rules as a fresh checkout (Amanda, 2026-10-20): credit lowers
// what a plan charges. The portal API checks it again (checkMonthlyPlan).
export const payInFullOnly = (items: any[]) =>
  (items || []).some((item: any) => {
    const step = Number(item && item.registrationStep) || 0;
    const onPlan =
      `${(item && item.planId) || ""}` != "" ||
      ((item && item.participants) || []).length > 0;
    return step == 4 || (step == 3 && onPlan);
  });
